import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import WebsiteFormLeadsSection from '../components/marketing/WebsiteFormLeadsSection';
import MarketingOrderFormSection from '../components/marketing/MarketingOrderFormSection';
import IntakeResponsesSection from '../components/marketing/IntakeResponsesSection';
import BusinessCardScannerSection from '../components/marketing/BusinessCardScannerSection';
import RoboticsProposalsSection from '../components/marketing/RoboticsProposalsSection';
import HospitalityInquiriesSection from '../components/marketing/HospitalityInquiriesSection';
import ProductInquiriesSection from '../components/marketing/ProductInquiriesSection';
import CampaignsSection from '../components/marketing/CampaignsSection';
import './Page.css';
import './TasksBoard.css';
import './Events.css';
import './Marketing.css';
import '../components/marketing/Campaigns.css';

const TABS = [
  { id: 'order-form', label: 'Marketing Order Form' },
  { id: 'website-leads', label: 'Website Form Leads' },
  { id: 'robotics-proposal', label: 'Robotics Proposal' },
  { id: 'hospitality', label: 'Hospitality' },
  { id: 'product-inquiries', label: 'Product Inquiries' },
  { id: 'intake-responses', label: 'Intake Form Responses' },
  { id: 'card-scanner', label: 'Business Card Scanner (Beta)' },
  { id: 'campaigns', label: 'Campaigns' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** Tabs only the marketing team (and admins) can open. */
const MARKETING_ONLY_TABS: TabId[] = [
  'website-leads',
  'robotics-proposal',
  'hospitality',
  'product-inquiries',
  'intake-responses',
  'card-scanner',
  'campaigns',
];

const Marketing: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const roles = Array.isArray(user?.roles) ? user!.roles : [];
  // Leads and intake forms are marketing's own tools; the order form is open to
  // the whole team.
  const hasMarketingTools = roles.includes('marketing') || roles.includes('admin');

  const activeTab = useMemo<TabId>(() => {
    const t = searchParams.get('tab');
    const known = TABS.some((tab) => tab.id === t) ? (t as TabId) : 'order-form';
    return MARKETING_ONLY_TABS.includes(known) && !hasMarketingTools ? 'order-form' : known;
  }, [searchParams, hasMarketingTools]);

  const selectTab = useCallback(
    (tab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const visibleTabs = TABS.filter((tab) => !MARKETING_ONLY_TABS.includes(tab.id) || hasMarketingTools);

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title="Marketing"
            subtitle="Marketing orders, website form leads and event intake responses"
          />
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view marketing data.</p>
            ) : (
              <>
                <div className="marketing-subtabs" role="tablist" aria-label="Marketing sections">
                  {visibleTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`marketing-subtab${
                        activeTab === tab.id ? ' marketing-subtab--active' : ''
                      }`}
                      onClick={() => selectTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === 'order-form' ? (
                  <MarketingOrderFormSection
                    userId={user.id}
                    defaultName={user.username}
                    defaultEmail={user.email}
                  />
                ) : null}
                {activeTab === 'website-leads' && hasMarketingTools ? (
                  <WebsiteFormLeadsSection userId={user.id} />
                ) : null}
                {activeTab === 'robotics-proposal' && hasMarketingTools ? (
                  <RoboticsProposalsSection userId={user.id} />
                ) : null}
                {activeTab === 'hospitality' && hasMarketingTools ? (
                  <HospitalityInquiriesSection userId={user.id} />
                ) : null}
                {activeTab === 'product-inquiries' && hasMarketingTools ? (
                  <ProductInquiriesSection userId={user.id} />
                ) : null}
                {activeTab === 'intake-responses' && hasMarketingTools ? (
                  <IntakeResponsesSection userId={user.id} />
                ) : null}
                {activeTab === 'card-scanner' && hasMarketingTools ? (
                  <BusinessCardScannerSection userId={user.id} />
                ) : null}
                {activeTab === 'campaigns' && hasMarketingTools ? (
                  <CampaignsSection userId={user.id} />
                ) : null}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Marketing;
